import { academyApi, type AcademyWorksheetShareBody } from '@mathflat/domain/Academy'
import type { ShareScope } from '@mathflat/domain/Content'
import {
  type Curriculum,
  curriculumApi,
  type NestedCurriculumsParams,
} from '@mathflat/domain/Curriculum'
import {
  type EntranceWorksheet as EntranceWorksheetData,
  entranceWorksheetApi,
} from '@mathflat/domain/EntranceWorksheet'
import type { RequestMeta } from '@mathflat/domain/http/client'
import {
  type ConceptWorksheetFilterBody,
  type CustomPaperWorksheetFilterBody,
  type GroupWorksheetCreationBody,
  makeWorksheetApi,
  type MakeWorksheetProblem,
  type MergedWorksheetFilterBody,
  type MyDbOriginalWorksheetFilterBody,
  type MyDbProblemDetail,
  type MyDbProblemSearchBody,
  type MyDbSimilarWorksheetFilterBody,
  type OneClickWorksheetFilterBody,
  type PublicWorkbookWorksheetFilterBody,
  type RecommendedProblemSearchBody,
  type SchoolTestPaperWorksheetFilterBody,
  type SchoolTestWorksheetFilterBody,
  type WorksheetCreationBody,
  type WorksheetDetail as MakeWorksheetDetail,
  type WorksheetFilterDraft,
  type WorksheetFilterId,
  type WorksheetPartUpdateBody,
} from '@mathflat/domain/MakeWorksheet'
import type { LearningHistoryXlsxGrade } from '@mathflat/domain/StudentHistory'
import { studentWorksheetApi } from '@mathflat/domain/StudentWorksheet'
import {
  type ArithmeticWorksheet,
  type ArithmeticWorksheetGroup,
  type ArithmeticWorksheetGroupListParams,
  type ArithmeticWorksheetListParams,
  type LeveledWorksheetListItem,
  type MyWorksheetListItem,
  type MyWorksheetListParams,
  type PracticeTestWorksheetListItem,
  type SatWorksheetListItem,
  type SchoolTestSimilarWorksheetCountParams,
  type SchoolTestSimilarWorksheetParams,
  type SharedCustomWorksheet,
  type SharedCustomWorksheetSearchParams,
  type TextbookWorksheetListItem,
  type Worksheet as WorksheetEntity,
  type WorksheetAccessModifierBody,
  worksheetApi,
  type WorksheetCopyBody,
  type WorksheetDesign as WorksheetDesignEntity,
  type WorksheetDesignBody,
  type WorksheetDesignDetail,
  type WorksheetDesignListParams,
  type WorksheetFileParams,
} from '@mathflat/domain/Worksheet'
import { omit } from 'lodash-es'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
import { CheckableColl } from 'src/models/CheckableColl'
import CheckableMap from 'src/models/CheckableMap'

import type { 학습지필터 } from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import type { DownloadWorksheetT } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { LessonClass, ReqParams } from '@/dto'
import {
  ArithmeticCurriculumKeyWorksheet,
  ArithmeticWorksheetColl,
  LegacyArithmeticWorksheet,
  MakeProblem,
  MakeProblemColl,
  MakeWorksheetFilterOption,
  Worksheet,
  WorksheetColl,
  WorksheetDesign,
  WorksheetDesignOption,
} from '@/dto'
import { LegacyArithmeticWorksheetColl } from '@/dto/Worksheet/LegacyArithmeticWorksheetColl'
import { guestAdmissionId } from '@/page/Lesson-prepare/PageEntranceTest/guestAdmission'
import { RouteName } from '@/router/RouteName'
import { createLatestRequestSignal } from '@/utils/axios/latestRequestSignal'
import { splitFilenameAndExtension } from '@/utils/download'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

type WorksheetListRow =
  | MyWorksheetListItem
  | LeveledWorksheetListItem
  | TextbookWorksheetListItem
  | SatWorksheetListItem
  | PracticeTestWorksheetListItem

type LessonMaterialListParams = ReturnType<typeof ReqParams.makeRequestParams>

// 디자인 옵션 화면은 템플릿 id 를 함께 들고 다니지만 저장 요청에는 싣지 않는다
export type WorksheetDesignSaveParams = WorksheetDesignBody & {
  designTemplateId: WorksheetDesign['id']
}

// 나의DB 원본·유사 필터만 문항을 싣는다
const getDraftMyDbProblemDetails = (draft: WorksheetFilterDraft) =>
  'myDbProblemDetails' in draft ? draft.myDbProblemDetails ?? undefined : undefined

class WorksheetController implements Controller {
  // maxios 의 useAbortController 처럼 같은 요청의 앞선 것을 버린다. 단원·유형별과 연산은 같은 엔드포인트라 함께 묶인다
  private latestSignal = {
    conceptFilter: createLatestRequestSignal('POST/worksheet/filter/concept'),
    publicWorkbookFilter: createLatestRequestSignal('POST/worksheet/filter/public-workbook'),
    filterDraft: createLatestRequestSignal('GET/worksheet/filter'),
    recommendedProblems: createLatestRequestSignal('POST/worksheet/problem'),
    problemCount: createLatestRequestSignal('GET/worksheet/problem/count'),
    worksheetCreation: createLatestRequestSignal('POST/worksheet'),
    entranceWorksheetCreation: createLatestRequestSignal('POST/creverse/entrance-worksheet'),
  }

  adapter = {
    // 다른 기관 학습지 검색 API(OpenSearch)는 메인 API 와 필드명이 일부 다르다.
    sharedCustomWorksheet: makeAdapter({
      from({ titlePrefix, createdAt, autoScored, ...rest }: SharedCustomWorksheet) {
        return new Worksheet({
          ...rest,
          titleTag: titlePrefix,
          createDatetime: createdAt,
          autoScorable: autoScored,
        })
      },
    }),
    worksheetColl: makeAdapter({
      from(response: WorksheetListRow[]) {
        const worksheetList = response.map((res) => new Worksheet(res))
        return new CheckableColl(CheckableMap.fromArr(worksheetList, 'id' as keyof Worksheet))
      },
    }),
    legacyArithmetic: makeAdapter({
      from(response: ArithmeticWorksheet) {
        return new LegacyArithmeticWorksheet(response)
      },
    }),
    worksheetDesign: makeAdapter({
      from(response: WorksheetDesignEntity) {
        return new WorksheetDesign(response)
      },

      fromDetail(response: WorksheetDesignDetail) {
        return new WorksheetDesignOption(response)
      },
    }),
    problem: makeAdapter({
      from(problem: MakeWorksheetProblem, nullableOrder: boolean) {
        return new MakeProblem({
          ...problem,
          _concept: problem.concept,
          tagTop: problem.tagTop,
          favorite: problem.favorite ?? false,
          defaultOrder: nullableOrder ? problem.defaultOrder : null,
          sequence: problem.concept.sequence,
          conceptId: problem.concept.id,
          conceptName: problem.concept.name,
          conceptKey: problem.concept.key,
          boxIndex: problem.problemSource?.id ?? null,
        })
      },
    }),
    myDbProblemDetail: makeAdapter({
      from(detail: MyDbProblemDetail) {
        return new MakeProblem({
          id: detail.detailId,
          type: detail.latestVersion?.type ?? detail.problemType ?? 'SINGLE_CHOICE',
          level: detail.level,
          conceptId: detail.conceptId,
          topicId: detail.topicId,
          subTopicId: detail.subTopicId,
          conceptName: detail.conceptName ?? '',
          problemImageUrl: detail.latestVersion?.problemImageUrl ?? '',
          answerImageUrl: detail.latestVersion?.answerImageUrl ?? '',
          solutionImageUrl: detail.latestVersion?.solutionImageUrl ?? '',
          autoScoredType: detail.latestVersion?.autoScoredType ?? 'IMPOSSIBLE',
          tagTop: detail.tagTop ?? null,
          boxIndex: detail.box?.index ?? null,
        })
      },
    }),
    designTemplateId: {
      from(response: MakeWorksheetDetail) {
        const { pdf } = response
        return {
          ...response,
          pdf: pdf && { ...pdf, designTemplateId: pdf.designTemplateId ?? EMPTY_NUMBER_ID },
        }
      },
      to(request: WorksheetDesignSaveParams) {
        const { designTemplateId, ...req } = request
        return req
      },
    },
    worksheetsByCurriculumKey: makeAdapter({
      from(curriculum: ArithmeticWorksheetGroup[]) {
        return curriculum.map((chapter) => {
          const arithmeticWorksheets = chapter.worksheet.map((worksheet) =>
            'assignList' in worksheet
              ? new Worksheet(worksheet)
              : new ArithmeticCurriculumKeyWorksheet(worksheet),
          )
          return {
            curriculumKey: chapter.curriculumKey,
            curriculumType: chapter.curriculumType,
            curriculumName: chapter.curriculumName,
            arithmeticWorksheetColl: new ArithmeticWorksheetColl(
              CheckableMap.fromArr(arithmeticWorksheets, 'id'),
            ),
          }
        })
      },
    }),
    chapters: makeAdapter({
      from(curriculum: Curriculum[]) {
        return curriculum.map((chapter) => {
          return {
            curriculumKey: chapter.key,
            curriculumName: chapter.name,
          }
        })
      },
    }),
  }

  // 화면 필터(ReqParams)는 값을 느슨한 레코드로 들고 있어 탭마다의 요청 타입으로 좁혀 넘긴다
  private list =
    <Params>(
      fetch: (
        params: Params,
        meta?: RequestMeta,
      ) => Promise<{
        content: WorksheetListRow[]
        last: boolean
      }>,
      meta?: RequestMeta,
    ) =>
    async (params: LessonMaterialListParams) => {
      const { content, last } = await fetch(params as Params, meta)
      return { coll: this.adapter.worksheetColl.from(content), last }
    }

  private withScope = (searchShareScope: ShareScope) => (params: LessonMaterialListParams) =>
    // mylist 진입 시 share-scope 필터 미주입 (myListId만으로 본인 학습지 조회)
    this.list(worksheetApi.getMyWorksheets, { mp_event_name: 'T31-01_내학습지조회_API' })(
      'myListId' in params ? params : { ...params, searchShareScope: [searchShareScope] },
    )

  private trash = (params: LessonMaterialListParams) =>
    this.list(worksheetApi.getMyWorksheets, { mp_event_name: 'T31-01_내학습지조회_API' })({
      ...params,
      searchShareScope: ['ACADEMY'],
      status: 'HIDDEN',
    } satisfies Partial<MyWorksheetListParams>)

  lessonMaterialFetch = {
    [RouteName.academyShareScopeWorksheet]: this.withScope('ACADEMY'),
    [RouteName.academyGroupShareScopeWorksheet]: this.withScope('ACADEMY_GROUP'),
    [RouteName.worksheetTrash]: this.trash,
    [RouteName.recommend]: this.list(worksheetApi.getSchoolPrepareWorksheets, {
      mp_event_name: 'T33-15_내신대비추천학습지조회_API',
    }),
    [RouteName.essayWorksheet]: this.list(worksheetApi.getSchoolPrepareEssayWorksheets),
    [RouteName.textbookWorksheet]: this.list(worksheetApi.getTextbookWorksheets, {
      mp_event_name: 'T31-01_내신대비교과서학습지조회_API',
    }),
    [RouteName.practiceTest]: this.list(worksheetApi.getPracticeTestWorksheets, {
      mp_event_name: 'T33-29_수능모의고사학습지조회_API',
    }),
    [RouteName.practiceTestPair]: this.list(worksheetApi.getPracticeTestWorksheets, {
      mp_event_name: 'T33-29_수능모의고사학습지조회_API',
    }),
    [RouteName.themeEntranceTest]: this.list(worksheetApi.getEntranceTestWorksheets, {
      mp_event_name: 'T33-49_입학TEST학습지조회_API',
    }),
    [RouteName.weeklyTest]: this.list(worksheetApi.getWeeklyTestWorksheets, {
      mp_event_name: 'T33-63_주간TEST학습지조회_API',
    }),
    [RouteName.chapterTest]: this.list(worksheetApi.getChapterTestWorksheets, {
      mp_event_name: 'T33-78_단원TEST학습지조회_API',
    }),
    [RouteName.generalTest]: this.list(worksheetApi.getChapterTestWorksheets, {
      mp_event_name: 'T33-78_단원TEST학습지조회_API',
    }),
    [RouteName.kmmExamWorksheet]: this.list(worksheetApi.getExamWorksheets),
    [RouteName.satWorksheet]: this.list(worksheetApi.getSatWorksheets),
    [RouteName.policeMilitaryPracticeTest]: this.list(
      worksheetApi.getUniversityPracticeTestWorksheets,
    ),
    [RouteName.policeMilitaryPracticeTestPair]: this.list(
      worksheetApi.getUniversityPracticeTestPairWorksheets,
    ),
  }

  fetchDesignTemplate = async () =>
    await worksheetApi.getWorksheetDesignTemplates({
      mp_event_name: 'T35-00_학습지디자인템플릿조회_API',
    })

  fetchLegacyArithmetic = async (params: ArithmeticWorksheetListParams) => {
    const { content, last } = await worksheetApi.getArithmeticWorksheets(params, {
      mp_event_name: 'T32-12_연산학습지조회_구_API',
    })
    const arithmeticArr = content.map(this.adapter.legacyArithmetic.from)

    return {
      arithmeticColl: new LegacyArithmeticWorksheetColl(CheckableMap.fromArr(arithmeticArr, 'id')),
      last,
    }
  }

  fetchShare = async (params: SharedCustomWorksheetSearchParams) => {
    const { content, last } = await worksheetApi.getSharedCustomWorksheets(
      // 목록은 facet·정확한 총건수를 쓰지 않는다
      { ...params, includeAggregations: false, exactTotal: false },
      { mp_event_name: 'T34-01_다른기관학습지조회_API' },
    )
    const problems = content.map(this.adapter.sharedCustomWorksheet.from)
    const workSheetMap = CheckableMap.fromArr(problems, 'id')
    return { workSheetColl: new WorksheetColl(workSheetMap), last }
  }

  // 학습지 상세 정보
  fetchWorksheetDetail = async (
    worksheetId: MakeWorksheetDetail['id'],
    ignoredForDeleted?: boolean,
  ) =>
    this.adapter.designTemplateId.from(
      await makeWorksheetApi.getWorksheetDetail(
        worksheetId,
        { ignoredForDeleted },
        { meta: { mp_event_name: 'T31-01_학습지상세조회_API' } },
      ),
    )

  // 학습지에 저장한 PDF 색상. 다운로드 중에 읽는 값이라 상세 조회 트래킹을 남기지 않는다
  fetchWorksheetPdfColor = async (
    worksheetId: MakeWorksheetDetail['id'],
    ignoredForDeleted?: boolean,
  ) =>
    (await makeWorksheetApi.getWorksheetDetail(worksheetId, { ignoredForDeleted })).pdf?.layoutColor

  getAssignedStudentInfoByWorksheetId = async (id: WorksheetEntity['id']) =>
    await worksheetApi.getWorksheetAssignees(id, { mp_event_name: 'T31-03_학습지다운로드_API' })

  // 디자인옵션 리스트
  fetchWorksheetDesign = async (params: WorksheetDesignListParams) => {
    const { content, last } = await worksheetApi.getWorksheetDesigns(params, {
      mp_event_name: 'T35-12_학습지만들기3_학습지디자인템플릿조회_API',
    })

    const worksheetDesignList = content.map(this.adapter.worksheetDesign.from)
    return {
      list: worksheetDesignList,
      last,
    }
  }

  fetchWorksheetDesignDetail = async (id: WorksheetDesign['id']) => {
    const detail = await worksheetApi.getWorksheetDesign(id, {
      mp_event_name: 'T35-12_학습지만들기3_특정학습지디자인템플릿조회_API',
    })
    return this.adapter.worksheetDesign.fromDetail(detail)
  }

  postWorksheetDesignDetail = async (data: WorksheetDesignSaveParams) => {
    await worksheetApi.createWorksheetDesign(this.adapter.designTemplateId.to(data), {
      mp_event_name: 'T35-12_학습지만들기3_학습지템플릿생성_API',
    })
  }

  putWorksheetDesignDetail = async (id: WorksheetDesign['id'], data: WorksheetDesignSaveParams) => {
    await worksheetApi.updateWorksheetDesign(id, this.adapter.designTemplateId.to(data), {
      mp_event_name: 'T35-12_학습지만들기3_학습지템플릿수정_API',
    })
  }

  deleteWorksheetDesignDetail = async (id: WorksheetDesign['id']) => {
    await worksheetApi.deleteWorksheetDesign(id, {
      mp_event_name: 'T35-12_학습지만들기3_학습지템플릿삭제_API',
    })
  }

  // 1단계 - 학습지 만들기 : filterId를 통해 학습지 문제 수 조회
  fetchProblemCountByFilterId = async (filterId: WorksheetFilterId) =>
    await makeWorksheetApi.getRecommendedProblemCount(filterId, {
      signal: this.latestSignal.problemCount({ useAbortController: true }),
    })

  // 2단계 - 내 문제 > 나의DB 탭 문제 목록. 응답이 CommonResponse 래퍼 없는 배열이라 data 를 그대로 매핑한다
  fetchMyDbFilterProblem = async (conceptIdList: MyDbProblemSearchBody['conceptIdList']) => {
    const problems = await makeWorksheetApi.getMyDbProblems({ conceptIdList })
    return problems.map((problem) => this.adapter.problem.from(problem, false))
  }

  // 학습지 병합 - filterId 만들기
  createFilterIdByMergedWorksheet = async (params: MergedWorksheetFilterBody) =>
    await makeWorksheetApi.createMergedWorksheetFilter(params)

  // 1단계 - 단원유형별
  // 기존 ConceptWorksheetFilterBody 쓰이는 곳까지 파악하기 힘들어서 일단 제네릭 처리 ㅜ
  async createFilterIdByChapter(params: ConceptWorksheetFilterBody | 학습지필터.단원유형별필터) {
    const data = {
      conceptIdList: [],
      excludedTopicIds: [],
      excludedSubTopicIds: [],
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    return await makeWorksheetApi.createConceptWorksheetFilter(data as ConceptWorksheetFilterBody, {
      meta: { mp_event_name: 'T35-01_학습지만들기1_단원/유형별_학습지필터생성_API' },
      signal: this.latestSignal.conceptFilter({ useAbortController: true }),
    })
  }

  // 1단계 - 연산
  createFilterIdByArithmetic = async (
    params: Omit<ConceptWorksheetFilterBody, 'problemList'> &
      Partial<Pick<ConceptWorksheetFilterBody, 'problemList'>>,
  ) => {
    return await makeWorksheetApi.createConceptWorksheetFilter(
      // 재추출 payload(filterIdParams)는 problemList 를 싣지 않으므로 chapter 경로처럼 null 기본 주입
      { problemList: null, ...params, filterId: params.filterId ?? undefined },
      { signal: this.latestSignal.conceptFilter({ useAbortController: true }) },
    )
  }

  // 화살표 함수로 둔다 — 필터 서비스가 이 함수를 떼어 내 들고 다니므로 `this` 가 묶여 있어야 한다.
  postFilterBySupportedWorkbook = async (params: 학습지필터.지원교재필터) => {
    const adaptedParams: PublicWorkbookWorksheetFilterBody = {
      problemList: null,
      problemOption: 'ALL' as const,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      // 서버는 비어 있으면 기본값(빈 집합)을 쓰고 null 은 받지 않는다
      exampleProblemIds: params.exampleProblemIds ?? undefined,
      pairX: params.pairX === 0 ? null : params.pairX,
      similarX: params.similarX === 0 ? null : params.similarX,
    }

    return await makeWorksheetApi.createPublicWorkbookWorksheetFilter(adaptedParams, {
      meta: { mp_event_name: 'T35-03_학습지만들기1_시중교재_학습지필터옵션생성_API' },
      signal: this.latestSignal.publicWorkbookFilter({ useAbortController: true }),
    })
  }

  // 수능 모의고사
  createFilterByPracticeTest = async (params: 학습지필터.수능모의고사필터) => {
    const data = {
      problemList: null,
      selectedConceptIdList: [],
      selectedLittleChapterIdList: [],
      ...params,
      filterId: params.filterId ?? undefined,
    }
    return await makeWorksheetApi.createPracticeTestWorksheetFilter(data, {
      meta: { mp_event_name: 'T35-04_학습지만들기1_모의고사_필터생성변경시마다_API' },
    })
  }

  // 힉교별 내신 기출
  createFilterBySchoolTest = async (params: SchoolTestWorksheetFilterBody) => {
    const filter = {
      ...params,
      pairX: params.pairX === 0 ? null : params.pairX,
      similarX: params.similarX === 0 ? null : params.similarX,
    }

    return await makeWorksheetApi.createSchoolTestWorksheetFilter(filter)
  }
  // 기출 업로드 - 기타 학습지
  createFilterByCustomPaper = async (data: CustomPaperWorksheetFilterBody) =>
    await makeWorksheetApi.createCustomPaperWorksheetFilter(data, {
      meta: { mp_event_name: 'T35-05_학습지만들기1_기출업로드_기타학습지_필터생성_API' },
    })
  // 기출 업로드 : 기출 시험지
  createFilterBySchoolTestPaper = async (data: SchoolTestPaperWorksheetFilterBody) =>
    await makeWorksheetApi.createSchoolTestPaperWorksheetFilter(data, {
      meta: { mp_event_name: 'T35-06_학습지만들기1_기출업로드_기출시험지_필터생성_API' },
    })

  // 기출 업로드 v2 : 원본 학습지 필터 생성
  createOriginalFilterBySchoolTestPaperV2 = async (data: MyDbOriginalWorksheetFilterBody) =>
    await makeWorksheetApi.createMyDbOriginalWorksheetFilter(data, {
      meta: { mp_event_name: 'T35-06_학습지만들기1_기출업로드_기출시험지_필터생성_API' },
    })

  // 기출 업로드 v2 : 유사 학습지 필터 생성
  createSimilarFilterBySchoolTestPaperV2 = async (payload: MyDbSimilarWorksheetFilterBody) =>
    await makeWorksheetApi.createMyDbSimilarWorksheetFilter(payload, {
      meta: { mp_event_name: 'T35-06_학습지만들기1_기출업로드_기출시험지_필터생성_API' },
    })

  createFilterByOneClick = async (data: OneClickWorksheetFilterBody) =>
    await makeWorksheetApi.createOneClickWorksheetFilter(data, {
      meta: { mp_event_name: 'T99-01_학습지만들기_원클릭복습학습지_필터생성변경시마다_API' },
    })

  // 2단계 ------

  // UUID를 통한 학습지만들기 필터정보 가지고 오기
  fetchSearchFilterByFilterId = async (
    filterId: string,
    options?: { useAbortController?: boolean },
  ) => {
    const meta = { useAbortController: options?.useAbortController ?? true }
    const data = await makeWorksheetApi.getWorksheetFilterDraft(filterId, {
      meta: { mp_event_name: 'T35-01_학습지만들기1_단원유형별(필터생성변경시마다)_API' },
      signal: this.latestSignal.filterDraft(meta),
    })

    const option = new MakeWorksheetFilterOption(data)
    const myDbProblemDetails = getDraftMyDbProblemDetails(data)

    // API 응답에 myDbProblemDetailIds가 없을 경우에 대한 fallback
    if (myDbProblemDetails?.length && !option.myDbProblemDetailIds?.length) {
      option.update({ myDbProblemDetailIds: myDbProblemDetails.map((d) => d.detailId) })
    }

    /**
     * MY_DB_ORIGINAL 필터 특징
     * - /worksheet/problem을 호출하지 않음
     * - 원본 필터의 makeProblemColl 구성
     *   - problemList 정보가 있는 경우: 저장된 문제(problemList)기반으로 구성
     *   - problemList 정보가 없는 경우: myDbProblemDetails(실제 원본 문제)로 구성한다.
     */
    if (data.type === 'MY_DB_ORIGINAL') {
      // data.problemList가 있으면 올바른 problemId를 가진 문제로 사용 (수정/복제/재출제 경로)
      if (data.problemList && data.problemList.length > 0) {
        // problemIdToDetailIdMap을 활용하여 빈 problemSource를 보강 (불변 패턴)
        const { problemIdToDetailIdMap } = data
        const detailMap =
          problemIdToDetailIdMap && myDbProblemDetails?.length
            ? new Map(myDbProblemDetails.map((d) => [d.detailId, d]))
            : null

        const problemList = data.problemList
          .map((problem) => {
            if (!problem.problemSource && detailMap && problemIdToDetailIdMap) {
              const detailId = problemIdToDetailIdMap[String(problem.id)]
              const detail = detailId != null ? detailMap.get(detailId) : undefined
              if (detail) {
                return {
                  ...problem,
                  problemSource: { id: detailId, problemImageUrl: problem.problemImageUrl },
                }
              }
            }
            return problem
          })
          .map((problem) => this.adapter.problem.from(problem, true))
        return {
          option,
          makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
          myDbProblemDetails,
          rawProblemList: data.problemList,
          isRecommendation: false,
        }
      }

      // myDbProblemDetails를 myDbProblemDetailIds 순서로 정렬 (기출 업로드 새로 만들기 경로)
      if (myDbProblemDetails?.length && option.myDbProblemDetailIds?.length) {
        const orderMap = new Map(option.myDbProblemDetailIds.map((id, index) => [id, index]))
        myDbProblemDetails.sort((a, b) => {
          const orderA = orderMap.get(a.detailId) ?? Number.MAX_SAFE_INTEGER
          const orderB = orderMap.get(b.detailId) ?? Number.MAX_SAFE_INTEGER
          return orderA - orderB
        })
      }

      const problemList =
        myDbProblemDetails && myDbProblemDetails.length > 0
          ? myDbProblemDetails.map((detail) => this.adapter.myDbProblemDetail.from(detail))
          : ([] as MakeProblem[])

      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: data.problemList ?? null,
        isRecommendation: false,
      }
    }

    // 문제가 있으면 (수정)
    if (data.problemList && data.problemList.length) {
      const problemList = data.problemList.map((problem) =>
        this.adapter.problem.from(problem, true),
      )
      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: null,
        isRecommendation: false,
      }
    } else {
      // 문제가 없으면 (1단계 진입) - 유사문제 리스트를 호출해서 채워서 리턴
      const problems = await makeWorksheetApi.searchRecommendedProblems(
        { filterId },
        {
          meta: { mp_event_name: 'T35-01_학습지만들기1_학습지문제수조회_API' },
          signal: this.latestSignal.recommendedProblems(meta),
        },
      )

      const problemList = problems.map((problem) => this.adapter.problem.from(problem, true))

      return {
        option,
        makeProblemColl: new MakeProblemColl(CheckableMap.fromArr(problemList, 'id')),
        myDbProblemDetails,
        rawProblemList: null,
        isRecommendation: true,
      }
    }
  }

  fetchFilterMyDbProblemDetails = async (filterId: WorksheetFilterId) => {
    const draft = await makeWorksheetApi.getWorksheetFilterDraft(filterId)
    return getDraftMyDbProblemDetails(draft) ?? []
  }

  // (구) 유사문제, 새문제추가 -> (뉴) 새문제추가만
  fetchSimilarWorksheet = async (params: RecommendedProblemSearchBody) => {
    const problems = await makeWorksheetApi.searchRecommendedProblems(params, {
      meta: { mp_event_name: 'T35-01_학습지만들기1_학습지문제수조회_API' },
    })
    const problemList = problems.map((problem) => this.adapter.problem.from(problem, false))
    return new MakeProblemColl(CheckableMap.fromArr(problemList, 'id'))
  }

  // 3단계
  // 학습지 생성 / 복제후 생성
  postCreateMakeWorksheet = async (
    data: WorksheetCreationBody,
    options?: { useAbortController?: boolean },
  ) =>
    await makeWorksheetApi.createWorksheet(data, {
      meta: { mp_event_name: 'T35-12_학습지만들기3_학습지만들기버튼선택시_API' },
      signal: this.latestSignal.worksheetCreation({
        useAbortController: options?.useAbortController ?? true,
      }),
    })

  // 서버가 tag('입학고사')·shareScope(ACADEMY)를 고정하므로 일반 생성 페이로드에서 제거한다.
  // 동봉 배정은 제네릭 assignStudentIdList 에 합성 id 로 실려오므로 (guestId, admissionRefId)로 분해한다.
  postCreateEntranceTestWorksheet = async (
    data: WorksheetCreationBody,
    options?: { useAbortController?: boolean },
  ) =>
    await entranceWorksheetApi.createEntranceWorksheet(
      {
        ...omit(data, ['tag', 'prefix', 'assignStudentIdList', 'shareScope']),
        assignedTargets: (data.assignStudentIdList ?? [])
          .filter((id): id is string => id != null)
          .map(guestAdmissionId.parse),
      },
      {
        signal: this.latestSignal.entranceWorksheetCreation({
          useAbortController: options?.useAbortController ?? true,
        }),
      },
    )

  // 서버가 tag·shareScope 등을 고정하므로 생성과 동일하게 제거해 보낸다
  putEntranceTestWorksheet = async (
    worksheetId: EntranceWorksheetData['id'],
    data: WorksheetCreationBody,
  ) => {
    await entranceWorksheetApi.updateEntranceWorksheet(
      worksheetId,
      omit(data, ['tag', 'prefix', 'assignStudentIdList', 'shareScope']),
    )
  }

  // 배정된 시험지 3단계 수정 — PUT과 동일하게 서버가 고정하는 tag·prefix는 제거해 보낸다
  patchEntranceTestWorksheet = async (
    worksheetId: EntranceWorksheetData['id'],
    data: WorksheetPartUpdateBody,
  ) => {
    await entranceWorksheetApi.updateEntranceWorksheetPart(
      worksheetId,
      omit(data, ['tag', 'prefix']),
    )
  }

  // 그룹 오답 - 일괄 오답관리 - 학생별 오답학습지
  postGroupWrongWorksheetPerStudent = async (data: GroupWorksheetCreationBody) => {
    await makeWorksheetApi.createGroupWorksheets(data, {
      meta: { mp_event_name: 'T42-11_개별오답학습지필터_필터생성변경시마다_API' },
    })
  }

  // 학습지 수정 — PUT 스키마(CustomWorksheetUpdateRequest)에 shareScope가 없어 서버가 무시하므로 제거해 보낸다
  putMakeWorksheet = async (
    worksheetId: MakeWorksheetDetail['id'],
    data: WorksheetCreationBody,
  ) => {
    await makeWorksheetApi.updateWorksheet(worksheetId, omit(data, ['shareScope']), {
      meta: { mp_event_name: 'T35-12_학습지만들기3_학습지수정하기선택시_API' },
    })
  }
  // 출제 된 학습지 수정
  patchMakeWorksheet = async (
    worksheetId: MakeWorksheetDetail['id'],
    data: WorksheetPartUpdateBody,
  ) => {
    await makeWorksheetApi.updateWorksheetPart(worksheetId, data, {
      meta: { mp_event_name: 'T35-12_학습지만들기3_출제된학습지수정하기선택시_API' },
    })
  }
  fetchWorksheetDownload = async (
    params: WorksheetFileParams,
    type?: DownloadWorksheetT['type'],
  ) => {
    const { presignedUrl, fileName } = await worksheetApi.getWorksheetFile(
      params,
      type !== 'ARITHMETIC' && type !== 'ARITHMETIC_ELEMENTARY'
        ? { mp_event_name: 'T31-03_학습지다운로드_API' }
        : undefined,
    )

    const { fileExt } = splitFilenameAndExtension(fileName)
    return { url: presignedUrl, fullFileName: fileName, fileExt }
  }

  deleteWorksheets = async (worksheetIds: WorksheetEntity['id'][]) =>
    await worksheetApi.deleteWorksheets(worksheetIds, { mp_event_name: 'T31-01_학습지삭제_API' })

  forceDeleteWorksheets = async (worksheetIds: WorksheetEntity['id'][]) =>
    await worksheetApi.forceDeleteWorksheets(worksheetIds)

  deleteWorksheetsInTrash = async (worksheetIds: WorksheetEntity['id'][]) =>
    await worksheetApi.deleteWorksheetsInTrash(worksheetIds)

  emptyWorksheetInTrash = async () => await worksheetApi.emptyWorksheetTrash()

  patchWorksheetShared = async (data: AcademyWorksheetShareBody) =>
    await academyApi.updateAcademyWorksheetShareSetting(data, {
      mp_event_name: 'T31-01_학습지공개여부수정_API',
    })

  copyWorksheet = async (worksheetId: WorksheetEntity['id'], requestBody: WorksheetCopyBody) => {
    await worksheetApi.copyWorksheet(worksheetId, requestBody)
  }

  fetchArithmeticWorksheetsByCurriculumKey = async (params: ArithmeticWorksheetGroupListParams) => {
    const { content } = await worksheetApi.getArithmeticWorksheetGroups(
      { ...params, page: params.page || 0, size: params.size || 999999 },
      { mp_event_name: 'T32-12_연산학습지조회_API' },
    )
    return this.adapter.worksheetsByCurriculumKey.from(content)
  }

  fetchChapters = async (params: NestedCurriculumsParams) => {
    const data = await curriculumApi.getNestedCurriculums(params, {
      mp_event_name: 'T99-01_학습지만들기1단계_범위조회필터_API',
    })
    return this.adapter.chapters.from(data)
  }

  patchAccessModifier = async (requestBody: WorksheetAccessModifierBody) => {
    await worksheetApi.updateWorksheetAccessModifier(requestBody, {
      mp_event_name: 'T31-01_학습지학생앱공개여부수정_API',
    })
  }

  getGradeXLS = async (
    { schoolType, grade }: LearningHistoryXlsxGrade,
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return studentWorksheetApi.getGradeWorksheetStatisticsSheet({ schoolType, grade }, dateParams)
  }

  getClassXLS = async (
    classId: LessonClass['id'],
    dateParams: {
      startYear: number
      endYear: number
      startWeek: number
      endWeek: number
      periodDescription: string
    },
  ) => {
    return studentWorksheetApi.getLessonClassWorksheetStatisticsSheet(classId, dateParams)
  }

  // 학교별 기출 유사 학습지 검색
  fetchSimilarWorksheetsBySchoolTest = async (params: SchoolTestSimilarWorksheetParams) =>
    await worksheetApi.getSchoolTestSimilarWorksheets(params)

  // 학교별 기출 유사 학습지 수량 검색
  fetchWorksheetCountBySchoolTest = async (params: SchoolTestSimilarWorksheetCountParams) =>
    await worksheetApi.getSchoolTestSimilarWorksheetCount(params)

  // 학습지 복구
  restoreWorksheet = async (worksheetIds: WorksheetEntity['id'][]) => {
    await worksheetApi.restoreWorksheets(worksheetIds)
  }
}

export const worksheetController = new WorksheetController()
